import { createScreenText } from 'helpers/screenText';
const st = createScreenText('ticket_return_reconciliation_messages');

// src/slices/ticketReturnReconciliation/types.ts
// Kiểu dữ liệu dùng chung cho tài nguyên TicketReturnReconciliation (phiếu đối soát thu hồi vé ế).
// Module này CHỈ chứa type — 2 slice tiêu thụ nó: ticketReturnAppraisal (US-018, trang Thẩm định)
// và ticketReturnWarehouseReport (US-019, trang Lưu kho & Báo cáo).
//
// Toàn bộ tên field bám đúng DTO thật của ticket-service:
//   TicketService.Contacts/Dtos/TicketReturnReconciliationDto.cs
//   TicketService/Features/TicketReturns/TicketReturnStatuses.cs

// Trạng thái phiếu đối soát — CHỈ 2 giá trị int (TicketReturnReconciliationStatuses).
// Không có "TuChoi"/"YeuCauDoiSoatLai" ở đây: 2 quyết định đó nằm ở field `decision`.
// YeuCauDoiSoatLai giữ Status = ChoThamDinh (mở lại bảng để đếm lại); PheDuyet/TuChoi -> DaThamDinh.
export const RECONCILIATION_STATUS = {
  ChoThamDinh: 1,
  DaThamDinh: 2,
} as const;

export type ReconciliationStatus =
  (typeof RECONCILIATION_STATUS)[keyof typeof RECONCILIATION_STATUS];

// Tên/màu chỉ dùng làm fallback — DTO đã trả sẵn `statusName` + `statusBadgeColor`.
export const RECONCILIATION_STATUS_LABELS: Record<string, string> = {
  1: st('cho_tham_dinh_text'),
  2: st('da_tham_dinh_text'),
};

export const RECONCILIATION_STATUS_COLORS: Record<string, string> = {
  1: 'warning',
  2: 'success',
};

// Query param `Status` nhận cả tên ("ChoThamDinh"/"DaThamDinh") lẫn số ("1"/"2") —
// TicketReturnReconciliationStatuses.Parse. Dùng dạng tên cho dễ đọc trên URL.
export const RECONCILIATION_STATUS_QUERY = {
  ChoThamDinh: 'ChoThamDinh',
  DaThamDinh: 'DaThamDinh',
} as const;

export const RECONCILIATION_STATUS_OPTIONS = [
  { value: 'All', label: st('tat_ca_trang_thai_label') },
  { value: RECONCILIATION_STATUS_QUERY.ChoThamDinh, label: st('cho_tham_dinh_text') },
  { value: RECONCILIATION_STATUS_QUERY.DaThamDinh, label: st('da_tham_dinh_text') },
];

// Quyết định thẩm định (US-018) — string, khớp TicketReturnDecisions
export const APPRAISAL_DECISION = {
  PheDuyet: 'PheDuyet',
  TuChoi: 'TuChoi',
  YeuCauDoiSoatLai: 'YeuCauDoiSoatLai',
} as const;

export type AppraisalDecision = (typeof APPRAISAL_DECISION)[keyof typeof APPRAISAL_DECISION];

export const APPRAISAL_DECISION_LABELS: Record<string, string> = {
  PheDuyet: st('phe_duyet_text'),
  TuChoi: st('tu_choi_text'),
  YeuCauDoiSoatLai: st('yeu_cau_doi_soat_lai_text'),
};

export const APPRAISAL_DECISION_COLORS: Record<string, string> = {
  PheDuyet: 'success',
  TuChoi: 'danger',
  YeuCauDoiSoatLai: 'warning',
};

/**
 * Ý kiến thẩm định bắt buộc khi KHÔNG phê duyệt — khớp validate phía BE
 * (AppraiseTicketReturnReconciliationCommandHandler: "Bat buoc nhap y kien khi khong phe duyet").
 */
export const isCommentRequired = (decision: AppraisalDecision | string): boolean =>
  decision !== APPRAISAL_DECISION.PheDuyet;

/** Giới hạn độ dài ý kiến thẩm định phía BE (MaxCommentLength = 500) */
export const APPRAISAL_COMMENT_MAX_LENGTH = 500;

// Kết quả đối chiếu từng vé khi quét đếm — int (TicketReturnMatchStatuses)
export const MATCH_STATUS = {
  HopLe: 1,
  KhongHopLe: 2,
  ChuaThay: 3,
} as const;

export type MatchStatus = (typeof MATCH_STATUS)[keyof typeof MATCH_STATUS];

// Fallback — DTO đã trả sẵn `matchStatusName`
export const MATCH_STATUS_LABELS: Record<string, string> = {
  1: st('hop_le_text'),
  2: st('khong_hop_le_text'),
  3: st('chua_thay_text'),
};

// Màu nền dòng: xanh = Hợp lệ, đỏ = Không hợp lệ, vàng = Chưa thấy
export const MATCH_STATUS_COLORS: Record<string, string> = {
  1: 'success',
  2: 'danger',
  3: 'warning',
};

// Lý do loại vé — string enum 4 giá trị cố định (TicketReturnRejectReasons).
// DTO trả kèm `rejectReasonName` tiếng Việt; danh sách này dùng cho dropdown nhân viên chủ động
// loại vé lúc quét.
export const REJECT_REASON = {
  RachMo: 'RachMo',
  SaiDaiLy: 'SaiDaiLy',
  SaiKy: 'SaiKy',
  KhongCoTrongDanhSach: 'KhongCoTrongDanhSach',
} as const;

export type RejectReason = (typeof REJECT_REASON)[keyof typeof REJECT_REASON];

export const REJECT_REASON_LABELS: Record<string, string> = {
  RachMo: st('rach_mo_text'),
  SaiDaiLy: st('sai_dai_ly_text'),
  SaiKy: st('sai_ky_text'),
  KhongCoTrongDanhSach: st('khong_co_trong_danh_sach_text'),
};

export const REJECT_REASON_OPTIONS = [
  { value: '', label: st('de_he_thong_tu_doi_chieu_label') },
  { value: REJECT_REASON.RachMo, label: st('rach_mo_text') },
  { value: REJECT_REASON.SaiDaiLy, label: st('sai_dai_ly_text') },
  { value: REJECT_REASON.SaiKy, label: st('sai_ky_text') },
  { value: REJECT_REASON.KhongCoTrongDanhSach, label: st('khong_co_trong_danh_sach_text') },
];

// 1 dòng vé trong phiếu đối soát — TicketReturnReconciliationLineDto
export interface TicketReturnReconciliationLine {
  id: number;
  ticketNumber: string;
  symbol: string;
  matchStatus: MatchStatus | number;
  matchStatusName?: string | null;
  rejectReason?: string | null;
  rejectReasonName?: string | null;
  scannedAt: string;
}

// Phiếu đối soát thu hồi vé ế — TicketReturnReconciliationDto
export interface TicketReturnReconciliation {
  id: number;
  reconciliationCode: string;

  returnRequestId: number;
  returnRequestCode?: string | null;

  // BE chỉ trả agentId (logical FK sang system-service) — tên/mã đại lý map ở FE qua agentLookup
  agentId: number;

  actualReceivedQuantity: number;
  rejectedQuantity: number;

  attachmentFileKey?: string | null;
  attachmentFileName?: string | null;
  // Signed URL sinh on-demand khi GET (TTL 1h) — không có endpoint file-url riêng
  attachmentUrl?: string | null;

  note?: string | null;

  status: ReconciliationStatus | number;
  statusName?: string | null;
  statusBadgeColor?: string | null;
  // false = phiếu đang ở giai đoạn quét đếm tại quầy (chưa "lập phiếu" chính thức)
  isFrozen: boolean;

  createdAt?: string | null;
  createdBy?: number | null;
  createdByName?: string | null;

  decision?: string | null;
  appraisalComment?: string | null;
  appraisedBy?: number | null;
  appraisedByName?: string | null;
  appraisedAt?: string | null;

  warehouseLocation?: string | null;
  assignedAt?: string | null;

  lines: TicketReturnReconciliationLine[];
}

// 1 dòng báo cáo nhanh (US-019) — TicketReturnReportRowDto
export interface TicketReturnReportRow {
  agentId: number;
  issuancePlanId?: number | null;
  planCode?: string | null;
  issuedQuantity: number;
  returnedQuantity: number;
  rejectedQuantity: number;
  returnRatePercent: number;
  // Luôn null tới khi finance-service consume TicketReturnApprovedMessage
  revenue?: number | null;
  debtAdjustment?: number | null;
}

// Tổng hợp các dòng báo cáo — FE tự cộng (BE trả mảng phẳng, không kèm summary)
export interface TicketReturnReportSummary {
  issuedQuantity: number;
  returnedQuantity: number;
  rejectedQuantity: number;
  returnRatePercent: number;
}

export interface ReconciliationPagination {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}
